import {
  Box,
  Button,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import axios from "../axios";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { queryClient } from "../index";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";

const MarkAttendance = () => {
  const { classID } = useParams();
  const navigate = useNavigate();
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [attendance, setAttendance] = useState({});

  const { data, isLoading, isFetching } = useQuery(
    ["classAttendance", classID, date],
    () =>
      axios
        .get(`/attendance/class/${classID}`, { params: { date } })
        .then((res) => res.data),
    {
      onSuccess: (data) => {
        // Pre-fill attendance from existing records
        const initial = {};
        data.students.forEach((s) => {
          if (s.status) {
            initial[s.id] = s.status;
          }
        });
        setAttendance(initial);
      },
    }
  );

  const submitMutation = useMutation((payload) =>
    axios.post(`/attendance/class/${classID}`, payload)
  );

  const handleStatusChange = (studentID, newStatus) => {
    if (newStatus !== null) {
      setAttendance((prev) => ({ ...prev, [studentID]: newStatus }));
    }
  };

  const handleSubmit = () => {
    const records = Object.entries(attendance).map(([studentID, status]) => ({
      studentID: parseInt(studentID),
      status,
    }));

    if (records.length === 0) {
      toast.error("Please mark attendance for at least one student");
      return;
    }

    submitMutation.mutate(
      { date, records },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["classAttendance", classID, date]);
          toast.success("Attendance saved successfully");
        },
        onError: (err) => {
          toast.error(err.response?.data?.message || "Error saving attendance");
        },
      }
    );
  };

  const handleMarkAllPresent = () => {
    if (!data) return;
    const all = {};
    data.students.forEach((s) => {
      all[s.id] = "PRESENT";
    });
    setAttendance(all);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Button
            variant="text"
            onClick={() => navigate("/attendance")}
            sx={{ mb: 1 }}
          >
            Back to classes
          </Button>
          <Typography variant="h5" component="h2">
            {data?.className} - Attendance
          </Typography>
        </Box>
      </Box>

      <Box display="flex" gap={2} alignItems="center" mb={3}>
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleMarkAllPresent}
        >
          Mark All Present
        </Button>
        {isFetching && <CircularProgress size={20} />}
      </Box>

      {data && data.students.length === 0 && (
        <Typography color="textSecondary">
          No students found in this class.
        </Typography>
      )}

      {data && data.students.length > 0 && (
        <>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Student Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.students.map((student) => (
                  <TableRow key={student.id}>
                    <TableCell>{student.fullName}</TableCell>
                    <TableCell align="center">
                      <ToggleButtonGroup
                        value={attendance[student.id] || null}
                        exclusive
                        onChange={(_, val) =>
                          handleStatusChange(student.id, val)
                        }
                        size="small"
                      >
                        <ToggleButton
                          value="PRESENT"
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "#4caf50",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#388e3c" },
                            },
                          }}
                        >
                          Present
                        </ToggleButton>
                        <ToggleButton
                          value="ABSENT"
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "#f44336",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#d32f2f" },
                            },
                          }}
                        >
                          Absent
                        </ToggleButton>
                        <ToggleButton
                          value="LATE"
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "#ff9800",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#f57c00" },
                            },
                          }}
                        >
                          Late
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={submitMutation.isLoading}
          >
            {submitMutation.isLoading ? "Saving..." : "Save Attendance"}
          </Button>
        </>
      )}
    </div>
  );
};

export default MarkAttendance;
